import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, Loader2, ThumbsUp, ThumbsDown, Home } from "lucide-react";
import { toast } from "sonner";

// Uses REACT_APP_BACKEND_URL for public API
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicReplyPage() {
  const { poolId } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState("choose"); // choose | form | success | already | error
  const [form, setForm] = useState({ name: "", district: "", loan_type: "", amount: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/public/pool/${poolId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => {
        setEntry(d);
        if (d.status === "converted") { setScreen("already"); setResult({ lead_id: d.converted_lead_id }); }
        setLoading(false);
      })
      .catch(() => { setScreen("error"); setLoading(false); });
  }, [poolId]);

  const send = async (reply, extra = {}) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/pool-reply/${poolId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reply, ...extra }),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setResult(data);
      setScreen(reply === "yes" ? "success" : "declined");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  const submitForm = () => {
    if (form.name.trim()) {
      send("yes", { ...form, amount: form.amount ? Number(form.amount) : 0 });
    } else {
      toast.error("Please tell us your name");
    }
  };

  if (loading) {
    return <Wrap><Loader2 className="w-8 h-8 animate-spin text-amber-500"/></Wrap>;
  }

  if (screen === "error") {
    return <Wrap><ErrorScreen/></Wrap>;
  }

  if (screen === "already" || screen === "success") {
    return <Wrap>
      <div className="text-center max-w-md" data-testid="reply-success">
        <CheckCircle2 className="w-20 h-20 text-emerald-400 mx-auto mb-4"/>
        <div className="font-heading text-3xl text-white mb-2">Nandri! Thank You!</div>
        <div className="text-slate-400">Our team will call you shortly on <span className="font-mono-alt text-amber-400">{entry?.phone}</span>.</div>
        {result?.lead_id && <div className="mt-4 text-xs text-slate-500 font-mono-alt">Ref: {result.lead_id}</div>}
        <Link to="/" className="mt-8 inline-flex items-center gap-2 text-sm text-amber-400 hover:underline"><Home className="w-4 h-4"/>Shakthi Home</Link>
      </div>
    </Wrap>;
  }

  if (screen === "declined") {
    return <Wrap>
      <div className="text-center max-w-md" data-testid="reply-declined">
        <XCircle className="w-20 h-20 text-slate-500 mx-auto mb-4"/>
        <div className="font-heading text-3xl text-white mb-2">Sari! No problem.</div>
        <div className="text-slate-400">We won't contact you again. If you change your mind, our website is always open.</div>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 text-sm text-amber-400 hover:underline"><Home className="w-4 h-4"/>Shakthi Home</Link>
      </div>
    </Wrap>;
  }

  return (
    <Wrap>
      <div className="w-full max-w-md" data-testid="public-reply-page">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest text-amber-500 mb-2">Shakthi Consultancy</div>
          <div className="font-heading text-3xl text-white mb-2">Loan venuma?</div>
          <div className="text-slate-400 text-sm">
            Hi {entry?.name || `friend at ${entry?.phone?.slice(-4)}`}, <span className="text-amber-400">{entry?.uploaded_by_name || "our team"}</span> shared your number with us.
            Do you want us to help with a loan application?
          </div>
        </div>

        {screen === "choose" && (
          <div className="space-y-3">
            <Button onClick={() => setScreen("form")} data-testid="reply-yes-btn"
              disabled={submitting}
              className="w-full h-16 text-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-2xl">
              <ThumbsUp className="w-6 h-6 mr-2"/> Yes! Loan Venum
            </Button>
            <Button onClick={() => send("no")} data-testid="reply-no-btn"
              disabled={submitting}
              variant="secondary"
              className="w-full h-16 text-lg bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-2xl">
              <ThumbsDown className="w-6 h-6 mr-2"/> No thanks
            </Button>
            <div className="text-[10px] text-center text-slate-600 mt-6">
              Ref: <span className="font-mono-alt">{poolId}</span>
            </div>
          </div>
        )}

        {screen === "form" && (
          <div className="card-surface p-6 space-y-3">
            <div className="text-slate-400 text-sm mb-2">Few quick details so we can help you better:</div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-widest">Your Name <span className="text-red-400">*</span></Label>
              <Input data-testid="reply-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-slate-950 border-slate-700 mt-2"/>
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-widest">Place / City</Label>
              <Input data-testid="reply-district" value={form.district} onChange={e => setForm({ ...form, district: e.target.value })} className="bg-slate-950 border-slate-700 mt-2"/>
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-widest">Loan Type</Label>
              <Input data-testid="reply-loan" placeholder="Home, Personal, Business..." value={form.loan_type} onChange={e => setForm({ ...form, loan_type: e.target.value })} className="bg-slate-950 border-slate-700 mt-2"/>
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-widest">Amount Needed (₹)</Label>
              <Input data-testid="reply-amount" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value.replace(/\D/g, "") })} className="bg-slate-950 border-slate-700 mt-2"/>
            </div>
            <Button onClick={submitForm} disabled={submitting} data-testid="reply-submit"
              className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full mt-2">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin"/> : "Submit & Get Call"}
            </Button>
            <button onClick={() => setScreen("choose")} className="text-xs text-slate-500 hover:text-slate-300 w-full text-center pt-2">Back</button>
          </div>
        )}
      </div>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6 font-heading">
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-transparent pointer-events-none"/>
      <div className="relative z-10 w-full flex flex-col items-center">{children}</div>
    </div>
  );
}

function ErrorScreen() {
  return (
    <div className="text-center max-w-md">
      <XCircle className="w-20 h-20 text-red-400 mx-auto mb-4"/>
      <div className="font-heading text-3xl text-white mb-2">Invalid Link</div>
      <div className="text-slate-400">This link has expired or the reference is not found.</div>
      <Link to="/" className="mt-8 inline-flex items-center gap-2 text-sm text-amber-400 hover:underline"><Home className="w-4 h-4"/>Shakthi Home</Link>
    </div>
  );
}
